import functions from '@/utils/functions';

/**
 * Given a windowSize, return a function that can create a sliding window of that size on a given
 * dataset.
 *
 * @param windowSize size of the window
 * @param dataset dataset to construct the sliding window on
 */
const sliding = (windowSize: number) => <T>(dataset: T[]): T[][] => {
  if (!Number.isInteger(windowSize) || dataset.length < windowSize || windowSize <= 0) {
    throw new Error(
      `Cannot construct sliding window of size ${windowSize} and dataset length ${dataset.length}`,
    );
  } else if (dataset.length === windowSize) {
    return [dataset];
  } else {
    const indexedDataset: [T, number][] = dataset.map((t: T, i: number) => [t, i]);
    const firstWindow = [dataset.slice(0, windowSize)];
    const addWindow = (ts: T[]) => (
      windowsSoFar: T[][],
      tWithIndex: [T, number],
    ): T[][] => {
      const nextWindow = ts.slice(tWithIndex[1], tWithIndex[1] + windowSize);
      return [
        ...windowsSoFar,
        nextWindow,
      ];
    };
    const windows = indexedDataset
      .slice(1, indexedDataset.length - (windowSize - 1))
      .reduce(
        addWindow(dataset),
        firstWindow,
      );
    return windows;
  }
};

/**
 * Given a windowSize, a dataset, and a numeric property extractor, extract the numeric property and
 * compute a sliding average over the numeric values.
 *
 * @param windowSize size of the window
 * @param dataset dataset to construct the sliding window on
 * @param prop function that extracts a numeric property from an element of the dataset
 */
const slidingAverage = <T>(windowSize: number, dataset: T[], prop: (t: T) => number): number[] => {
  const numericDataset = dataset.map(prop);
  const slidingWindow = sliding(windowSize)(numericDataset);
  const averages = slidingWindow.map(functions.average);
  return averages;
};

const simpleMovingAverage = (windowSize: number) => function* generator(
  dataset: number[],
): Generator<number, void, void> {
  if (!Number.isInteger(windowSize) || dataset.length < windowSize || windowSize <= 0) {
    throw new Error(
      `Cannot construct sliding window of size ${windowSize} and dataset length ${dataset.length}`,
    );
  }
  let index = 0;
  while (index + windowSize <= dataset.length) {
    if (windowSize === 1) {
      yield dataset[index];
    } else {
      yield functions.average(dataset.slice(index, index + windowSize));
    }
    index += 1;
  }
};

export default {
  simpleMovingAverage,
  sliding,
  slidingAverage,
};
